import { createSignal, onCleanup } from "solid-js";
import type { Position } from "../../shared/api";
import { type Fix, kmhFrom } from "../speed";

/** Until the device answers: Bordeaux centre (Pey-Berland), the test region. */
export const DEFAULT_CENTER: Position = { lat: 44.8378, lng: -0.5792 };

/** granted: a fix arrived; denied: refused (site or system setting); insecure: plain HTTP. */
export type GeoStatus =
  | "pending"
  | "granted"
  | "denied"
  | "unavailable"
  | "unsupported"
  | "insecure";

const [position, setPosition] = createSignal<Position>(DEFAULT_CENTER);
const [located, setLocated] = createSignal(false);
const [status, setStatus] = createSignal<GeoStatus>("pending");

/** GeolocationPositionError.code: 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT. */
export const geoStatusFor = (code: number): GeoStatus => (code === 1 ? "denied" : "unavailable");

/** Some devices report 0,0 before their first real fix: that is not a position. */
export const isUsableFix = (p: Position): boolean =>
  Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0);

const [speedKmh, setSpeedKmh] = createSignal(0);
const [fixAt, setFixAt] = createSignal(0);
let lastFix: Fix | undefined;

const onFix = (p: GeolocationPosition) => {
  const fix = { lat: p.coords.latitude, lng: p.coords.longitude };
  if (!isUsableFix(fix)) return;
  const f: Fix = { ...fix, at: p.timestamp || Date.now(), speed: p.coords.speed };
  setSpeedKmh(kmhFrom(lastFix, f));
  setFixAt(Date.now());
  lastFix = f;
  setPosition(fix);
  setLocated(true);
  setStatus("granted");
};
const onError = (e: GeolocationPositionError) => {
  // A timeout after a fix keeps the last position and status.
  if (!located() || e.code === 1) setStatus(geoStatusFor(e.code));
};

/** The browser API, or why it can't be used (geolocation needs HTTPS, except on localhost). */
function device():
  | Pick<Geolocation, "getCurrentPosition" | "watchPosition" | "clearWatch">
  | undefined {
  if (typeof window !== "undefined" && !window.isSecureContext) {
    setStatus("insecure");
    return undefined;
  }
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    setStatus("unsupported");
    return undefined;
  }
  return navigator.geolocation;
}

const OPTIONS = { enableHighAccuracy: true, maximumAge: 5_000, timeout: 10_000 };

/** Starts watching the device position (asks permission); falls back to DEFAULT_CENTER. */
export function watchPosition() {
  const g = device();
  if (!g) return;
  const id = g.watchPosition(onFix, onError, OPTIONS);
  onCleanup(() => g.clearWatch(id));
}

/**
 * A current fix, asked now (prompts if needed: call it from a tap on iOS). Resolves with the last
 * known position on refusal or after `timeoutMs`, so a ride can still start.
 */
export function freshPosition(
  timeoutMs = 4_000,
  g: Pick<Geolocation, "getCurrentPosition"> | undefined = device(),
): Promise<Position> {
  if (!g) return Promise.resolve(position());
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(position()), timeoutMs);
    g.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        onFix(p);
        resolve(position());
      },
      (e) => {
        clearTimeout(timer);
        onError(e);
        resolve(position());
      },
      { ...OPTIONS, maximumAge: 30_000, timeout: timeoutMs },
    );
  });
}

/** What to tell the rider when the position is missing; undefined when nothing is wrong. */
export function geoHint(s: GeoStatus, userAgent: string): string | undefined {
  if (s === "insecure") return "La localisation demande une connexion sécurisée (HTTPS).";
  if (s === "unsupported") return "Ce navigateur ne donne pas accès à la localisation.";
  if (s === "unavailable") return "Position introuvable pour le moment.";
  if (s !== "denied") return undefined;
  return /iPhone|iPad/.test(userAgent)
    ? "Localisation refusée. Activez-la dans Réglages › Confidentialité › Service de localisation › Sites web Safari, puis aA › Réglages du site web › Position."
    : "Localisation refusée. Autorisez-la dans les réglages du site de votre navigateur.";
}

/** The rider's speed from the phone's GPS, 0 when standing still or without a fix for 10 s. */
const speed = () => (Date.now() - fixAt() > 10_000 ? 0 : speedKmh());

export const geo = { position, located, status, speed };

export function distanceMeters(a: Position, b: Position): number {
  const R = 6371000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function walkLabel(meters: number): string {
  const m = Math.round(meters / 10) * 10;
  const min = Math.max(1, Math.round(meters / 80));
  return m >= 1000
    ? `${(m / 1000).toFixed(1).replace(".", ",")} km · ${min} min`
    : `${m} m · ${min} min`;
}
